import { Box, Chip, Grid, Typography } from "@mui/material";
import { settingsReportStyles } from "../../../styles/SettingsReport.styles";
import ClientPicker from "./ClientPicker";
import { PerformanceReportTypeEnum } from "helpers/types";
import AssetPicker from "./AssetPicker";
import DateSelector from "../Statistic/DateSelector";
import { useState, useCallback, useEffect } from "react";
import dayjs from "dayjs";
import { useReportSettings } from "./ReportSettingsContext";

interface Props {
  type: PerformanceReportTypeEnum;
  header?: string;
  subtitle?: string;
}

type DateRange = [string | null, string | null];

const SettingsReport = ({
  type,
  header = "Optic Equity Access Performance",
  subtitle = "OPTIC ASSET MANAGEMENT",
}: Props) => {
  const [dateRanges, setDateRanges] = useState<DateRange[]>([]);
  const { updateSettings } = useReportSettings();

  useEffect(() => {
    const period: [string, string] =
      dateRanges.length > 0 && dateRanges[0][0] && dateRanges[0][1]
        ? [
            dayjs(dateRanges[0][0]).format("YYYY-MM-DD"),
            dayjs(dateRanges[0][1]).format("YYYY-MM-DD"),
          ]
        : ["", ""];
    updateSettings({ period });
  }, [dateRanges, updateSettings]);

  const handleDateChange = useCallback((range: DateRange) => {
    const [startDate, endDate] = range;
    if (startDate && endDate) {
      setDateRanges((prev) => {
        const rangeKey = `${startDate}-${endDate}`;
        const exists = prev.some(
          ([s, e]) => `${s}-${e}` === rangeKey
        );
        if (exists) return prev;
        return [...prev, range].sort((a, b) => dayjs(a[0]).diff(dayjs(b[0])));
      });
    }
  }, []);

  const handleRemoveDateRange = useCallback((index: number) => {
    setDateRanges((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const formatDateRange = (range: DateRange) => {
    const [startDate, endDate] = range;
    if (startDate && endDate) {
      return `${dayjs(startDate).format("MM/DD/YYYY")} - ${dayjs(endDate).format("MM/DD/YYYY")}`;
    }
    return "";
  };

  return (
    <Box sx={settingsReportStyles.container}>
      <Grid container spacing={2} alignItems="flex-start">
        <Grid item xs={12} md={7}>
          <Typography
            variant="h3"
            component="h1"
            sx={settingsReportStyles.mainTitle}
          >
            {header}
          </Typography>
          <Typography
            variant="body1"
            component="h2"
            sx={settingsReportStyles.subtitle}
          >
            {subtitle}
          </Typography>
        </Grid>

        <Grid item xs={12} md={5}>
          <Box sx={settingsReportStyles.summaryBox}>
            <ClientPicker />

            <Box sx={settingsReportStyles.summaryItem}>
              <Typography component="span" sx={settingsReportStyles.label}>
                Period:
              </Typography>
              <DateSelector
                style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}
                onDateChange={handleDateChange}
                additionalComponents={
                  dateRanges.length > 0 ? (
                    <Box sx={settingsReportStyles.periodChipContainer}>
                      {dateRanges.map((range, index) => (
                        <Chip
                          key={`${range[0]}-${range[1]}`}
                          label={formatDateRange(range)}
                          onDelete={() => handleRemoveDateRange(index)}
                          size="small"
                          sx={settingsReportStyles.periodChip}
                        />
                      ))}
                    </Box>
                  ) : null
                }
              />
            </Box>
            <AssetPicker
              type={type}
            />
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default SettingsReport;
